import React, { useCallback, useEffect, useMemo, useState } from "react";
import Grid from "@mui/material/Grid2";
import { useParams } from "react-router-dom";

import CommonCard from "../../components/common_card";
import { TypographyTitle, TypographyBold } from "../../style/typography";
import ReportTimeline from "../../components/arrowbox";
import DamReportStepper from "../../components/dam_report_stepper";
import MailConfirm from "../../components/mail_confirm";
import ApprovalStatusInfo from "../../components/approval_status_info";
import ReportCountdown from "../../components/report_countdown";
import ReportStopDialog from "../../components/report_stop_dialog";
import SendReportDialog from "../../components/send_report_dialog";
import { BoxCenterStart } from "../../style/box";
import { BaseDialog } from "../../components/base_dialog";
import { useAuth } from "../../contexts/AuthContext";
import { APPROVAL_STATUS, ROLE_UUID, REPORTING_TIME } from "../../const/config";

import {
  SecondaryInspectionStep01,
  SecondaryInspectionStep02,
  SecondaryInspectionStep03,
  SecondaryInspectionStep04,
  SecondaryInspectionStep05,
  SecondaryInspectionAfterApprovalTask
} from "../../components/workflow_step";

import ErrorScreen from "../../components/error_screen";
import { commonStyles } from "../../style/common";
import { useInspectionLogic } from "../../hooks/use_inspection_logic";
import SkeletonLoading from "../../components/skeleton_loading";
import dayjs from 'dayjs';
import "dayjs/locale/ja";
import { SECONDARY_INSPECTION_TEXTS, SECONDARY_INSPECTION_AFTER_APPROVAL_TASK } from "../../const/inspection_texts";
import { postData } from "../../func/api";

dayjs.locale("ja");

const SecondaryInspection = () => {
  const { uuid } = useParams();
  const { currentUser } = useAuth();

  // State
  const {
    earthquake, setEarthquake, checkResult, approval,
    mailInfo, loading, open, setOpen, isChecked, setIsChecked,
    createComplete, setCreateComplete, checkSummary, setCheckSummary, checkSummaryPrime, setCheckSummaryPrime,
    mandatoryFacilitys, setMandatoryFacilitys, facilityDams, setFacilityDams,
    taskStatus, setTaskStatus, approvalStatus, isInputter,
    isReviewer, isReadOnly, isApprovedNotSent, isSent,
    isValidTask, handleRestart, reportDetailList, department
  } = useInspectionLogic(uuid, 'secondary');

  // ハンドラー
  const handleNext = useCallback(async () => {
    try {
      // taskStatus が 19 または 20 の場合のみ更新処理
      if (taskStatus === 19 || taskStatus === 20) {
        const payload = {
          table_name: "report_progress",
          column_name1: taskStatus === 19 ? "si_reported_at" : "si_reported_to_internal_at",
          column_name2: taskStatus === 19 ? "si_reported_by" : "si_reported_to_internal_by",
          earthquake: earthquake,
          user_uuid: currentUser.id
        };
        await postData("api/post_update_report_progress", payload);
      }

      // taskStatus に応じた画面遷移やステータス変更
      if (taskStatus === 19) setTaskStatus(20);
      else if (taskStatus === 20) setTaskStatus(21);
      else if (taskStatus === 21) {
        setTaskStatus(0);
        localStorage.setItem("taskStatus", "0");
        window.location.href = `/earthquake_detail/${uuid}`;
      }

    } catch (err) {
      console.error("更新失敗しました。：", err);
    } finally {
      // ダイアログを閉じる
      setOpen(false);
    }
  }, [taskStatus, earthquake, currentUser]);

  const showTitle = taskStatus === 18;
  const showApproval = approvalStatus !== APPROVAL_STATUS.INIT && taskStatus === 18;

  if (loading) return <SkeletonLoading />;

  if (!isValidTask) {
    return <ErrorScreen {...{ earthquake, open, onOpen: () => setOpen(true), onClose: () => setOpen(false), onRestart: handleRestart }} />;
  }

  return (
    <BoxCenterStart sx={commonStyles.page}>
      <Grid container spacing={7} columns={12} sx={commonStyles.container}>

        {/* ヘッダー部分 */}
        {showTitle && (
          <Grid size={{ xs: 12 }}>
            <ReportStopDialog initial_report_uuid={earthquake.initial_report_uuid} earthquake_uuid={earthquake.uuid} />
            <TypographyTitle>{earthquake.name}</TypographyTitle>
          </Grid>
        )}

        {showApproval && (
          <Grid size={{ xs: 12 }}>
            <TypographyBold>承認状況</TypographyBold>
            <CommonCard child={<ApprovalStatusInfo approval={approval} approvalStatus={approvalStatus} />} />
          </Grid>
        )}

        <DamReportStepper reportDetailList={reportDetailList} maxWidth={1100} />

        {/* 入力者モード */}
        {isInputter && (
          <>
            <SecondaryInspectionStep01 title={SECONDARY_INSPECTION_TEXTS.inputter.step01.title} lead={SECONDARY_INSPECTION_TEXTS.inputter.step01.lead} descriptions={SECONDARY_INSPECTION_TEXTS.inputter.step01.descriptions} earthquake={earthquake} setEarthquake={setEarthquake} isReadOnly={isReadOnly} />
            <SecondaryInspectionStep02 title={SECONDARY_INSPECTION_TEXTS.inputter.step02.title} lead={SECONDARY_INSPECTION_TEXTS.inputter.step02.lead} descriptions={SECONDARY_INSPECTION_TEXTS.inputter.step02.descriptions} checkSummary={checkSummary} setCheckSummary={setCheckSummary} checkResult={checkResult} earthquake={earthquake} isReadOnly={isReadOnly} />
            <SecondaryInspectionStep03 title={SECONDARY_INSPECTION_TEXTS.inputter.step03.title} lead={SECONDARY_INSPECTION_TEXTS.inputter.step03.lead} descriptions={SECONDARY_INSPECTION_TEXTS.inputter.step03.descriptions} checkSummary={checkSummary} setCheckSummary={setCheckSummary} isReadOnly={isReadOnly} />
            <SecondaryInspectionStep04 title={SECONDARY_INSPECTION_TEXTS.inputter.step04.title} lead={SECONDARY_INSPECTION_TEXTS.inputter.step04.lead} descriptions={SECONDARY_INSPECTION_TEXTS.inputter.step04.descriptions} earthquake={earthquake} checkResult={checkResult} checkSummary={checkSummary} setCreateComplete={setCreateComplete} checkSummaryDataPrime={checkSummaryPrime} isCreateExcel={true} />
            <SecondaryInspectionStep05 title={SECONDARY_INSPECTION_TEXTS.inputter.step05.title} lead={SECONDARY_INSPECTION_TEXTS.inputter.step05.lead} earthquake={earthquake} checkResult={checkResult} checkSummary={checkSummary} createComplete={createComplete} isApproveRequest />
          </>
        )}

        {/* レビュアーモード */}
        {isReviewer && (
          <>
            <SecondaryInspectionStep01 title={SECONDARY_INSPECTION_TEXTS.reviewer.step01.title} lead={SECONDARY_INSPECTION_TEXTS.reviewer.step01.lead} descriptions={SECONDARY_INSPECTION_TEXTS.reviewer.step01.descriptions} earthquake={earthquake} setEarthquake={setEarthquake} isReadOnly={isReadOnly} />
            <SecondaryInspectionStep02 title={SECONDARY_INSPECTION_TEXTS.reviewer.step02.title} lead={SECONDARY_INSPECTION_TEXTS.reviewer.step02.lead} descriptions={SECONDARY_INSPECTION_TEXTS.reviewer.step02.descriptions} checkSummary={checkSummary} setCheckSummary={setCheckSummary} checkResult={checkResult} earthquake={earthquake} isReadOnly={isReadOnly} />
            <SecondaryInspectionStep03 title={SECONDARY_INSPECTION_TEXTS.reviewer.step03.title} lead={SECONDARY_INSPECTION_TEXTS.reviewer.step03.lead} descriptions={SECONDARY_INSPECTION_TEXTS.reviewer.step03.descriptions} checkSummary={checkSummary} setCheckSummary={setCheckSummary} isReadOnly={isReadOnly} />
            <SecondaryInspectionStep04 title={SECONDARY_INSPECTION_TEXTS.reviewer.step04.title} lead={SECONDARY_INSPECTION_TEXTS.reviewer.step04.lead} earthquake={earthquake} isCreateExcel={false} />
            <SecondaryInspectionStep05 title={SECONDARY_INSPECTION_TEXTS.reviewer.step05.title} lead={SECONDARY_INSPECTION_TEXTS.reviewer.step05.lead} descriptions={SECONDARY_INSPECTION_TEXTS.reviewer.step05.descriptions} earthquake={earthquake} isApproveRequest={false} />
          </>
        )}

        {/* メール確認・送信 */}
        {isApprovedNotSent && (
          <>
            <Grid size={{ xs: 12 }}><MailConfirm isChecked={isChecked} setIsChecked={setIsChecked} mail_info={mailInfo} department={department} /></Grid>
            <Grid size={{ xs: 12 }}><SendReportDialog uuid={earthquake.secondary_inspection_uuid} earthquake_uuid={earthquake.uuid} taskStatus={taskStatus} setTaskStatus={setTaskStatus} disabled={!isChecked} type="secondary" department={department} /></Grid>
          </>
        )}

        {/* 送信後タスク */}
        {isSent && taskStatus === 19 && <SecondaryInspectionAfterApprovalTask title={SECONDARY_INSPECTION_AFTER_APPROVAL_TASK.status19.title} lead={SECONDARY_INSPECTION_AFTER_APPROVAL_TASK.status19.lead} appealContent={SECONDARY_INSPECTION_AFTER_APPROVAL_TASK.status19.appealContent} onClickRequestButton={() => setOpen(true)} />}
        {isSent && taskStatus === 20 &&
          <SecondaryInspectionAfterApprovalTask
            title={SECONDARY_INSPECTION_AFTER_APPROVAL_TASK.status20.title}
            appealContent={SECONDARY_INSPECTION_AFTER_APPROVAL_TASK.status20.appealContent}
            lead={SECONDARY_INSPECTION_AFTER_APPROVAL_TASK.status20.lead}
            listItems={SECONDARY_INSPECTION_AFTER_APPROVAL_TASK.status20.listItems}
            onClickRequestButton={() => setOpen(true)} />}
        {isSent && taskStatus === 21 && <SecondaryInspectionAfterApprovalTask title={SECONDARY_INSPECTION_AFTER_APPROVAL_TASK.status21.title} lead={SECONDARY_INSPECTION_AFTER_APPROVAL_TASK.status21.lead} buttonContent={SECONDARY_INSPECTION_AFTER_APPROVAL_TASK.status21.buttonContent} onClickRequestButton={() => setOpen(true)} />}

        <BaseDialog open={open} onClose={() => setOpen(false)} title="業務実施確認" message={`次の業務を実施します。\nよろしいですか？`} onConfirm={handleNext} confirmText="OK" cancelText="キャンセル" />
        <ReportCountdown occued_at={earthquake.occured_at} addedMinutes={REPORTING_TIME.SECONDARYCHECK} />
      </Grid>
    </BoxCenterStart>
  );
};

export default SecondaryInspection;